import { inject, Injectable, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { HttpService } from '@utils/services/http.service';
import { UrlService } from '@utils/services/url-service.service';
import { Alarm } from '@interfaces/alarm';
import { ApiModuleEnum } from '@utils/interfaces/api-module.constant';
import { ServerListResponse } from '@utils/interfaces/serverResponses';
import { catchError, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AlarmsService {
  private readonly _alarmList = signal<Alarm[]>([]);
  public readonly alarmList = this._alarmList.asReadonly();

  private readonly _http = inject(HttpService);
  private readonly _urlService = inject(UrlService);

  private readonly URL = this._urlService.getEndpointUrl({
    module: ApiModuleEnum.ALARMS,
    version: 'v1',
    path: 'alarm',
  });

  public getAlarmsData(
    page = 1,
    size = 10,
    params = {},
  ): Observable<ServerListResponse<Alarm[]>> {
    return this._http.listPagination<ServerListResponse<Alarm[]>>(
      page,
      size,
      params,
      this.URL,
    );
  }

  public getNotificationList(
    page = 1,
    size = 5,
    params = {},
  ): Observable<ServerListResponse<Alarm[]>> {
    const url = `${this.URL}/notification`;

    return this._http
      .listPagination<ServerListResponse<Alarm[]>>(page, size, params, url)
      .pipe(
        tap(({ data }) => {
          this._alarmList.set(data);
        }),
        catchError(() => of({ count: 0, data: [] })),
      );
  }

  public getWithId(id: string | number): Observable<Alarm> {
    return this._http.getWithId<Alarm>({ url: this.URL, id });
  }

  public updatePartial(
    id: string | number,
    body: Partial<Alarm>,
    params?: HttpParams,
  ): Observable<void> {
    return this._http.updatePartial<Partial<Alarm>, void>(
      this.URL,
      id,
      body,
      params,
    );
  }

  public create(body: Alarm, params?: HttpParams): Observable<void> {
    return this._http.POST<void>({ url: this.URL, body, params });
  }
}
